import { useState } from "react";
import {
  Edit,
  SimpleForm,
  TextInput,
  ReferenceInput,
  AutocompleteInput,
  useTranslate,
  useEditContext,
  BooleanInput
} from "react-admin";
import { usePhotoUploadSubmitEdit } from "hooks/usePhotoUploadSubmitEdit";
import CustomHeaderActions from "./CustomHeaderActions";
import ShowAndEditDetails from "./ShowAndEditDetails";
import {
  DefaultAvatarIcon,
  ShowButtonIcon,
  StaffDetailsIcon
} from "assets/Icons";
import CustomImageInputSmall from "./CustomImageInputSmall";
import { CustomToolbar } from "./CustomToolbar";
import { CustomButton } from "./CustomButton";
import { Link } from "react-router-dom";
import { useParams } from "react-router-dom";

const InstagramAuthorizationButton = () => {
  const { record } = useEditContext();
  const translate = useTranslate();
  const handleAuthorize = async () => {
    const response = await fetch(
      `${process.env.REACT_APP_API_CORE_URL}/v1/artists/`
    );
    const { authorizeUrl } = await response.json();
    window.location.href = `${authorizeUrl}&state=${record.id}`;
  };

  return (
    <>
      {record.instagram_id && (
        <div className="text-[14px]">
          {translate("artists.already_authorized_instagram_account")}
        </div>
      )}
      {!record.instagram_id && (
        <CustomButton
          disabled={record.instagram_id}
          text="Authorize Instagram"
          onClick={handleAuthorize}
          variant="secondary"
          sx={{
            "@media screen and (max-width:640px)": {
              width: "100%"
            }
          }}
        />
      )}
    </>
  );
};

export default function ArtistEdit() {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { submit } = usePhotoUploadSubmitEdit({
    redirect: `/dashboard/Artist`,
    resource: "Artist",
    keys: ["cover_photo_url"],
    setIsLoading: setIsLoading
  });
  const translate = useTranslate();
  const { id } = useParams<{ id: string }>();

  return (
    <Edit
      actions={<CustomHeaderActions title={translate("pagenames.artist")} />}
    >
      <SimpleForm
        onSubmit={(data) => submit(data)}
        toolbar={<CustomToolbar isLoading={isLoading} />}
      >
        <ShowAndEditDetails
          title={translate("artists.artist_details")}
          view="edit"
          icon={<StaffDetailsIcon />}
          customButtons={
            <div className="flex items-center gap-3 sm:gap-2 flex-wrap w-full sm:w-auto">
              <InstagramAuthorizationButton />
              <Link
                to={`/dashboard/Artist/${id}/show`}
                style={{ width: "inherit" }}
              >
                <CustomButton
                  text={translate("common.show")}
                  variant="secondary"
                  startIcon={<ShowButtonIcon />}
                  sx={{
                    "@media screen and (max-width:640px)": {
                      width: "100%"
                    }
                  }}
                />
              </Link>
            </div>
          }
        />
        <BooleanInput
          source="is_active"
          label={translate("artists.active")}
          fullWidth
        />
        <ReferenceInput source="user_id" reference="User">
          <AutocompleteInput
            optionText={(value) => `${value.first_name} ${value.last_name}`}
            defaultValue={(value: any) =>
              `${value.first_name} ${value.last_name}`
            }
            label={translate("common.user")}
            fullWidth
            disabled
          />
        </ReferenceInput>
        <TextInput
          source="bio"
          label={translate("artists.bio")}
          fullWidth
          multiline
        />
        <div className="text-[14px] text-[rgba(0,0,0,0.6)] font-['Inter']">
          {translate("artists.artist_profile_photo")}:
        </div>
        <CustomImageInputSmall
          source="cover_photo_url"
          borderRadius="20px"
          buttonText={translate("common.set_photo")}
          bg="#B0ACA6"
          placeholder={<DefaultAvatarIcon />}
        />
      </SimpleForm>
    </Edit>
  );
}
